import { useCallback } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { Warranty } from "../presentation/components/warranty-card";
import type { VariantOption, VariantOptionAxis } from "../presentation/components/option-card";
import type { VariantRowEdit } from "../presentation/components/variant-pricing-row";
import { useBrands } from "./useBrands";
import { useCategories } from "./useCategories";
import { useCountries } from "./useCountries";
import { useProductDealTypes } from "./useProductDealTypes";
import { useProductWarranties } from "./useProductWarranties";
import { useProductWarrantyTypes } from "./useProductWarrantyTypes";
import { useProductScreenSizes } from "./useProductScreenSizes";
import { useProductResolutions } from "./useProductResolutions";
import { useProductRams } from "./useProductRams";
import { useProductStorageValues } from "./useProductStorageValues";
import { useProductColors } from "./useProductColors";
import { useProductCpus } from "./useProductCpus";
import { useProductConditions } from "./useProductConditions";
import { useProductWarrantyProviders } from "./useProductWarrantyProviders";
import { MOCK_NEW_VARIANT_EDITS } from "../mock-data";

interface UseDebugMyProductAutofillParams {
  setImages: Dispatch<SetStateAction<string[]>>;
  setTemplateImageFiles: Dispatch<SetStateAction<File[]>>;
  setName: Dispatch<SetStateAction<string>>;
  setBrandId: Dispatch<SetStateAction<string>>;
  setCategoryId: Dispatch<SetStateAction<string>>;
  setSku: Dispatch<SetStateAction<string>>;
  setCountryOfOriginId: Dispatch<SetStateAction<string>>;
  setDealTypeId: Dispatch<SetStateAction<string>>;
  setScreenSizeId: Dispatch<SetStateAction<string>>;
  setResolutionId: Dispatch<SetStateAction<string>>;
  setDatasheetUrl: Dispatch<SetStateAction<string>>;
  setNotes: Dispatch<SetStateAction<string>>;
  setDescription: Dispatch<SetStateAction<string>>;
  setWarranties: Dispatch<SetStateAction<Warranty[]>>;
  setVariantsEnabled: Dispatch<SetStateAction<boolean>>;
  setOptions: Dispatch<SetStateAction<VariantOption[]>>;
  setVariantEdits: Dispatch<SetStateAction<Record<string, VariantRowEdit>>>;
  setRemovedVariantKeys: Dispatch<SetStateAction<Set<string>>>;
  setVisibility: Dispatch<SetStateAction<boolean>>;
}

function pickRandom<T>(items: T[]): T | undefined {
  if (items.length === 0) return undefined;
  const index = Math.floor(Math.random() * items.length);
  return items[index];
}

function buildCombos(options: VariantOption[]) {
  const active = options.filter((option) => option.name && option.valueIds.length > 0);
  if (active.length === 0) return [];

  type Cell = { axis: VariantOptionAxis; valueId: string };
  const matrix: Cell[][] = active.map((option) =>
    option.valueIds.map((valueId) => ({ axis: option.name, valueId })),
  );

  const combos = matrix.reduce<Cell[][]>(
    (acc, row) => acc.flatMap((combo) => row.map((cell) => [...combo, cell])),
    [[]],
  );

  return combos
    .map((combo) => ({
      key: combo.map((cell) => `${cell.axis}:${cell.valueId}`).join("|"),
      cells: combo,
    }))
    .filter((combo) => combo.key.length > 0);
}

export function useDebugMyProductAutofill({
  setImages,
  setTemplateImageFiles,
  setName,
  setBrandId,
  setCategoryId,
  setSku,
  setCountryOfOriginId,
  setDealTypeId,
  setScreenSizeId,
  setResolutionId,
  setDatasheetUrl,
  setNotes,
  setDescription,
  setWarranties,
  setVariantsEnabled,
  setOptions,
  setVariantEdits,
  setRemovedVariantKeys,
  setVisibility,
}: UseDebugMyProductAutofillParams) {
  const isDebug = import.meta.env.DEV;
  const { data: brands } = useBrands();
  const { data: categories } = useCategories();
  const { data: countries } = useCountries();
  const { data: dealTypes } = useProductDealTypes();
  const { data: warranties } = useProductWarranties();
  const { data: warrantyTypes } = useProductWarrantyTypes();
  const { data: warrantyProviders } = useProductWarrantyProviders();
  const { data: screenSizes } = useProductScreenSizes();
  const { data: resolutions } = useProductResolutions();
  const { data: rams } = useProductRams();
  const { data: storageValues } = useProductStorageValues();
  const { data: colors } = useProductColors();
  const { data: cpus } = useProductCpus();
  const { data: conditions } = useProductConditions();

  const handleDevAutofill = useCallback(() => {
    if (!isDebug) return;

    const brand = pickRandom(brands ?? []);
    const category = pickRandom(categories ?? []);
    const country = pickRandom(countries ?? []);
    const dealType = pickRandom(dealTypes ?? []);
    const warranty = pickRandom(warranties ?? []);
    const warrantyType = pickRandom(warrantyTypes ?? []);
    const warrantyProvider = pickRandom(warrantyProviders ?? []);
    const screenSize = pickRandom(screenSizes ?? []);
    const resolution = pickRandom(resolutions ?? []);
    const condition = pickRandom(conditions ?? []);

    const axisSources: Array<{
      name: VariantOptionAxis;
      items: Array<{ id: number; name: string }> | undefined;
    }> = [
      { name: "RAM", items: rams },
      { name: "Storage", items: storageValues },
      { name: "Color", items: colors },
      { name: "CPU", items: cpus },
    ];

    const chosenAxes = axisSources.filter((axis) => (axis.items?.length ?? 0) > 0).slice(0, 2);
    const nextOptions: VariantOption[] = chosenAxes.map((axis, index) => ({
      id: index + 1,
      name: axis.name,
      valueIds: (axis.items ?? []).slice(0, 3).map((item) => String(item.id)),
    }));
    setImages([]);
    setTemplateImageFiles([]);
    setName((brand?.name ?? "Debug") + " " + (category?.name ?? "Product"));
    setBrandId(brand?.id != null ? String(brand.id) : "");
    setCategoryId(category?.id != null ? String(category.id) : "");
    setSku(`DBG-${Math.floor(Math.random() * 9000 + 1000)}`);
    setCountryOfOriginId(country?.id != null ? String(country.id) : "");
    setDealTypeId(dealType?.id != null ? String(dealType.id) : "");
    setScreenSizeId(screenSize?.id != null ? String(screenSize.id) : "");
    setResolutionId(resolution?.id != null ? String(resolution.id) : "");
    setDatasheetUrl("https://example.com/debug-product-datasheet.pdf");
    setNotes("DEV autofill generated for local testing. Remove before submitting.");
    setDescription(
      "Autofilled debug product for exercising the full create flow, including variants, pricing tiers, warranties, and uploads.",
    );
    setWarranties([
      {
        id: 1,
        warrantyId: warranty?.id != null ? String(warranty.id) : "",
        warrantyTypeId: warrantyType?.id != null ? String(warrantyType.id) : "",
        warrantyProviderId: warrantyProvider?.id != null ? String(warrantyProvider.id) : "",
      },
    ]);
    setVariantsEnabled(true);
    setOptions(nextOptions.length > 0 ? nextOptions : [{ id: 1, name: "", valueIds: [] }]);
    setRemovedVariantKeys(new Set());
    setVisibility(true);

    const variantCombos = buildCombos(nextOptions);
    const variantMocks = Object.values(MOCK_NEW_VARIANT_EDITS);
    const variantEdits = Object.fromEntries(
      variantCombos.map((combo, index) => {
        const mock = variantMocks[index % variantMocks.length];
        return [
          combo.key,
          {
            sku: mock?.sku ?? `DBG-${index + 1}`,
            stock: mock?.stock ?? String(12 + index * 5),
            conditionId: condition?.id != null ? String(condition.id) : "",
            model: `Debug Model ${index + 1}`,
            imageFile: undefined,
            imagePreviewUrl: undefined,
            pricingTiers: (() => {
              const base = 1500 + index * 350 + Math.floor(Math.random() * 200);
              return [
                { id: 1, minQty: "1", price: String(base) },
                { id: 2, minQty: "5", price: String(Math.round(base * 0.92)) },
                { id: 3, minQty: "10", price: String(Math.round(base * 0.85)) },
              ];
            })(),
          } satisfies VariantRowEdit,
        ] as const;
      }),
    );
    setVariantEdits(variantEdits);
  }, [
    isDebug,
    brands,
    categories,
    countries,
    dealTypes,
    warranties,
    warrantyTypes,
    warrantyProviders,
    screenSizes,
    resolutions,
    rams,
    storageValues,
    colors,
    cpus,
    conditions,
    setImages,
    setTemplateImageFiles,
    setName,
    setBrandId,
    setCategoryId,
    setSku,
    setCountryOfOriginId,
    setDealTypeId,
    setScreenSizeId,
    setResolutionId,
    setDatasheetUrl,
    setNotes,
    setDescription,
    setWarranties,
    setVariantsEnabled,
    setOptions,
    setVariantEdits,
    setRemovedVariantKeys,
    setVisibility,
  ]);

  return { isDebug, handleDevAutofill };
}
